import { MouseEvent, useState } from 'react';
import { Form } from 'antd';

import Image from 'global/Image';
import useMultiPortfolioStore from 'lib/stores/multiPorto';
import CompanyIcon from 'features/multi-porto/components/CompanyLogo';
import { lotSize } from 'global/TradingModal/constant';
import { lotFormatter } from 'utils/securities/formatter';

import BaseSecuritiesModal, {
  BaseSecuritiesModalProps,
} from 'global/Securities/components/Modal/BaseSecuritiesModal';

import {
  Container,
  NextButton,
  ConfirmInfoContainer,
  StockListContainer,
  StockItem,
  StockName,
  StockLot,
  PortfolioContainer,
  PortfolioInfo,
  PortfolioName,
  PortfolioInfoText,
} from './stock-confirm.styled';

export default function TransferStockConfirm({
  onCancel = () => {},
  onSubmit = () => {},
  ...props
}: Readonly<BaseSecuritiesModalProps>) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);

  const moveStockForm = useMultiPortfolioStore((s) => s.moveStockForm);
  const postMoveStock = useMultiPortfolioStore((s) => s.postMoveStock);
  const resetMoveStockForm = useMultiPortfolioStore(
    (s) => s.resetMoveStockForm,
  );

  const handleReset = () => {
    resetMoveStockForm();
  };

  const handleSubmit = async () => {
    setLoading(true);
    const payload = {
      ...moveStockForm,
      stocks_data: moveStockForm.stocks_data?.map((stock) => ({
        stock_code: stock.stock_code,
        shares: stock.shares,
      })),
    };

    delete payload.from_acc_name;
    delete payload.to_acc_name;

    try {
      const result = await postMoveStock(payload);
      if (typeof result === 'boolean' && result) {
        onSubmit();
      }
    } catch (err) {
      console.error('Error moving stock:', err);
    }

    setLoading(false);
  };

  const handleCancel = (e: MouseEvent<HTMLElement>) => {
    onCancel(e);
    handleReset();
  };

  return (
    <BaseSecuritiesModal
      title="Transfer Stock"
      hideSubmit
      onCancel={handleCancel}
      className="modal-multi-porto"
      transitionName=""
      {...props}
    >
      <Form form={form} layout="vertical">
        <Container onClick={(e) => e.stopPropagation()}>
          <ConfirmInfoContainer>
            <StockListContainer>
              {moveStockForm.stocks_data?.map((stock) => (
                <StockItem key={stock.stock_code}>
                  <CompanyIcon
                    symbol={stock.stock_code}
                    avatar_url={stock.avatar_url}
                    size={20}
                  />
                  <StockName>{stock.stock_code}</StockName>
                  <StockLot>
                    {lotFormatter(stock.shares / lotSize)} Lot
                  </StockLot>
                </StockItem>
              ))}
            </StockListContainer>

            <PortfolioContainer>
              <PortfolioInfo>
                <PortfolioName>
                  {moveStockForm.from_acc_name || ''}
                </PortfolioName>
                <PortfolioInfoText>Source Portfolio</PortfolioInfoText>
              </PortfolioInfo>
              <Image
                src="/icon/chevron/right-double-primary.svg"
                alt=""
                width={16}
                height={16}
              />
              <PortfolioInfo>
                <PortfolioName>{moveStockForm.to_acc_name || ''}</PortfolioName>
                <PortfolioInfoText>Destination Portfolio</PortfolioInfoText>
              </PortfolioInfo>
            </PortfolioContainer>
          </ConfirmInfoContainer>

          <Form.Item style={{ marginBottom: 0 }}>
            <NextButton
              onClick={handleSubmit}
              type="primary"
              disabled={loading}
              loading={loading}
            >
              Confirm
            </NextButton>
          </Form.Item>
        </Container>
      </Form>
    </BaseSecuritiesModal>
  );
}
